
import React, { useState, useRef, useCallback } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  Node,
  BackgroundVariant,
  EdgeTypes,
} from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { FlowSidebar } from "@/components/automation/FlowSidebar";
import { NodeConfigPanel } from "@/components/automation/NodeConfigPanel";
import nodeTypes from "@/components/automation/NodeRegistry";
import { CustomEdge } from "@/components/automation/edges/CustomEdge";
import { useAuth } from "@/hooks/useAuth";
import { NodeType, AutomationNode, AutomationNodeData } from "@/types/automation";
import { supabase } from "@/integrations/supabase/client";
import { nodeTypeConfigurations } from "@/components/automation/NodeRegistry";

import "@xyflow/react/dist/style.css";

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

const defaultEdgeOptions = {
  type: "custom",
  style: { strokeWidth: 2, stroke: "#b1b1b7" },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: "#b1b1b7",
  },
  animated: false,
};

const FlowBuilder = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<Node<AutomationNodeData> | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [currentFlowName, setCurrentFlowName] = useState("Novo Fluxo");
  const [currentFlowDesc, setCurrentFlowDesc] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { currentUser } = useAuth();
  const companyId =
    currentUser?.companyId || localStorage.getItem("currentCompanyId");
  const { toast } = useToast();

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        id: `e-${uuidv4()}`,
        type: "custom",
        animated: false,
        style: { strokeWidth: 2, stroke: "#b1b1b7" },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#b1b1b7",
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
      setIsModified(true);
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const type = event.dataTransfer.getData(
        "application/reactflow"
      ) as NodeType;
      const nodeConfig = nodeTypeConfigurations.find(
        (config) => config.type === type
      );

      if (!reactFlowInstance || !type || !nodeConfig) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: AutomationNode = {
        id: `${type}-${uuidv4()}`,
        type,
        position,
        data: {
          label: nodeConfig.label,
          description: nodeConfig.description,
          config: {},
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsModified(true);
    },
    [reactFlowInstance, setNodes]
  );

  const onDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    nodeType: string
  ) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<AutomationNodeData>);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodeUpdate = useCallback(
    (nodeId: string, data: AutomationNodeData) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...data,
              },
            };
          }
          return node;
        })
      );
      setIsModified(true);
    },
    [setNodes]
  );

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== nodeId));
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      );
      setSelectedNode(null);
      setIsModified(true);
    },
    [setNodes, setEdges]
  );

  const handleSaveFlow = useCallback(async () => {
    if (!companyId) {
      toast({
        title: "Erro",
        description: "ID da empresa não encontrado",
        variant: "destructive",
      });
      return;
    }

    if (nodes.length === 0) {
      toast({
        title: "Erro",
        description: "O fluxo está vazio",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      console.log("Tentando salvar fluxo...");
      
      const flowData = {
        name: currentFlowName,
        description: currentFlowDesc,
        nodes: nodes,
        edges: edges,
        is_active: false,
        company_id: companyId,
      };

      console.log("Dados do fluxo a serem salvos:", flowData);

      const { data, error } = await supabase
        .from('automation_flows')
        .insert({
          name: flowData.name,
          description: flowData.description,
          nodes: JSON.stringify(flowData.nodes),
          edges: JSON.stringify(flowData.edges),
          is_active: flowData.is_active,
          company_id: flowData.company_id,
        })
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      toast({
        title: "Sucesso",
        description: "Fluxo salvo com sucesso",
      });

      setIsModified(false);
      setIsSaving(false);
      
      if (data && data.length > 0) {
        console.log("Fluxo salvo com ID:", data[0].id);
      }
    } catch (error) {
      console.error("Erro ao salvar fluxo:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o fluxo. Tente novamente.",
        variant: "destructive",
      });
      setIsSaving(false);
    }
  }, [nodes, edges, currentFlowName, currentFlowDesc, companyId, toast]);

  const handleClearFlow = useCallback(() => {
    if (window.confirm("Tem certeza que deseja limpar o fluxo atual?")) {
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      setIsModified(true);
    }
  }, [setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      <FlowSidebar
        onDragStart={onDragStart}
        onSaveFlow={handleSaveFlow}
        onClearFlow={handleClearFlow}
        isFlowEmpty={nodes.length === 0}
        isFlowModified={isModified}
        isSaving={isSaving}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex space-x-4 items-center">
            <div>
              <Input
                value={currentFlowName}
                onChange={(e) => {
                  setCurrentFlowName(e.target.value);
                  setIsModified(true);
                }}
                className="font-medium w-64"
                placeholder="Nome do fluxo"
              />
            </div>
            <Input
              value={currentFlowDesc}
              onChange={(e) => {
                setCurrentFlowDesc(e.target.value);
                setIsModified(true);
              }}
              className="text-sm text-muted-foreground w-96"
              placeholder="Descrição do fluxo (opcional)"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={handleClearFlow}
              disabled={nodes.length === 0}
            >
              Limpar
            </Button>
            <Button
              onClick={handleSaveFlow}
              disabled={nodes.length === 0 || !isModified || isSaving}
            >
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>

        <div className="flex-1">
          <div className="h-full" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              fitView
            >
              <Controls />
              <MiniMap nodeStrokeWidth={3} zoomable pannable />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </div>
        </div>
      </div>

      <NodeConfigPanel
        selectedNode={selectedNode}
        onNodeUpdate={handleNodeUpdate}
        onNodeDelete={handleNodeDelete}
      />
    </div>
  );
};

const AutomationFlowPage = () => {
  return (
    <div className="h-[calc(100vh-4rem)]">
      <FlowBuilder />
    </div>
  );
};

export default AutomationFlowPage;
